import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { UNIDADES } from "@/componentes/inventario/FormularioArticulo"
import type {
  Disponibilidad,
  LineaSolicitud,
  OrdenDeLaSolicitud,
  Solicitud,
} from "@/componentes/suministros/tipos"
import { ESTADOS } from "@/componentes/suministros/tipos"
import { comoCantidad } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { paraElServidor } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"

/**
 * Cómo se dice la unidad de un renglón, en minúscula y al lado de la cifra.
 *
 * ⚠ **La unidad se enseña una sola vez, pegada a lo PEDIDO.** Repetirla en las tres
 * columnas —pedido, aprobado, entregado— triplica la palabra para decir lo mismo: una
 * solicitud no cambia de unidad por el camino.
 */
function comoUnidad(unidad: string, cuantos: string): string {
  const etiqueta = UNIDADES.find((u) => u.valor === unidad)?.etiqueta ?? unidad
  const singular = etiqueta.toLocaleLowerCase("es")
  return Number(cuantos) === 1 ? singular : `${singular}s`
}

/** Lo que falta por servir de un renglón, medido contra lo APROBADO. */
function pendiente(linea: LineaSolicitud): number {
  const objetivo = Number(linea.cantidad_aprobada ?? linea.cantidad)
  return Math.max(0, objetivo - Number(linea.cantidad_entregada))
}

/**
 * La ficha de una solicitud: lo que se pidió, lo que se decidió y lo que falta.
 *
 * ⚠ **Las dos salidas de una solicitud aprobada se ofrecen según lo que HAY**, no a
 * ciegas: si el depósito tiene existencia, lo que sale es «entregar»; si no, «comprarlo».
 * Ofrecer comprar lo que ya está guardado es justo el error que este módulo viene a
 * evitar, así que la pantalla lee la disponibilidad antes de enseñar nada.
 *
 * ⚠ **La orden de compra NO se redacta desde aquí con un clic ciego.** Este botón lleva a
 * «Órdenes de pago» con la solicitud ya citada, porque la orden necesita proveedor,
 * moneda y precios, y eso es el formulario de una orden — no un campo más en un modal de
 * pedidos. Lo que sí se hace aquí es dejar constancia de qué renglones van.
 */
export function FichaSolicitud({
  solicitudId,
  onCerrar,
  onCambio,
}: {
  solicitudId: number
  onCerrar: () => void
  onCambio: () => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [motivo, setMotivo] = useState("")
  const [rechazando, setRechazando] = useState(false)
  const [entregando, setEntregando] = useState<LineaSolicitud | null>(null)
  const [cuanto, setCuanto] = useState("")

  const solicitud = useQuery({
    queryKey: ["solicitud-suministro", solicitudId],
    queryFn: () => api.get<Solicitud>(`/api/v1/solicitudes-suministro/${solicitudId}`),
  })
  const ordenes = useQuery({
    queryKey: ["solicitud-suministro-ordenes", solicitudId],
    queryFn: () =>
      api.get<OrdenDeLaSolicitud[]>(`/api/v1/solicitudes-suministro/${solicitudId}/ordenes`),
  })

  const puedeAtender = tienePermiso(usuario, "suministro:atender")
  const puedeAprobar = tienePermiso(usuario, "suministro:aprobar")

  // Sólo quien atiende puede leerla: el endpoint pide `suministro:atender`.
  const existencias = useQuery({
    queryKey: ["solicitud-suministro-disponibilidad", solicitudId],
    queryFn: () =>
      api.get<Disponibilidad[]>(`/api/v1/solicitudes-suministro/${solicitudId}/disponibilidad`),
    enabled: puedeAtender && solicitud.data?.estado === "APROBADA",
  })

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["solicitud-suministro", solicitudId] })
    clienteQuery.invalidateQueries({
      queryKey: ["solicitud-suministro-disponibilidad", solicitudId],
    })
    onCambio()
  }

  const accion = useMutation({
    mutationFn: ({ que, cuerpo }: { que: string; cuerpo?: unknown }) =>
      api.post(`/api/v1/solicitudes-suministro/${solicitudId}/${que}`, cuerpo ?? {}),
    onSuccess: () => {
      setRechazando(false)
      setEntregando(null)
      setMotivo("")
      setCuanto("")
      refrescar()
    },
  })

  const s = solicitud.data
  const esMia = s !== undefined && s.solicitante_id === usuario?.id
  const hayDe = (lineaId: number) =>
    Number(existencias.data?.find((d) => d.linea_id === lineaId)?.existencia ?? 0)

  return (
    <Modal
      abierto={true}
      onCerrar={onCerrar}
      ancho="lg"
      titulo={s ? `${s.codigo} · ${ESTADOS[s.estado]}` : "Solicitud"}
    >
      {solicitud.isPending && <EstadoCarga etiqueta="Cargando la solicitud…" />}
      {solicitud.isError && (
        <EstadoError
          titulo="No se pudo cargar la solicitud."
          error={solicitud.error}
          onReintentar={() => solicitud.refetch()}
        />
      )}
      {s && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Insignia tono="neutro">{s.solicitante ?? "—"}</Insignia>
            {s.prioridad === "URGENTE" && <Insignia tono="peligro">Urgente</Insignia>}
            {s.autoaprobada && (
              <Insignia tono="aviso" title="La aprobó quien la pidió">
                Autoaprobada
              </Insignia>
            )}
            {s.necesaria_para && (
              <span style={{ color: "var(--fg-muted)" }}>Para el {s.necesaria_para}</span>
            )}
            {s.entregar_en && (
              <span style={{ color: "var(--fg-muted)" }}>Entregar en {s.entregar_en}</span>
            )}
          </div>

          {s.justificacion && <p className="text-sm">{s.justificacion}</p>}

          {s.motivo_rechazo && (
            <p className="tarjeta text-sm" style={{ color: "var(--destructive)" }}>
              Rechazada: {s.motivo_rechazo}
            </p>
          )}

          <Tabla etiqueta="Lo pedido">
            <Cabecera>
              <Th>Qué</Th>
              <Th derecha>Pedido</Th>
              <Th derecha>Aprobado</Th>
              <Th derecha>Entregado</Th>
              {puedeAtender && s.estado === "APROBADA" && <Th derecha>En depósito</Th>}
              {puedeAtender && s.estado === "APROBADA" && <Th derecha>Acción</Th>}
            </Cabecera>
            <Cuerpo>
              {s.lineas.map((linea) => (
                <Fila key={linea.id}>
                  <Td>
                    {linea.descripcion}
                    {linea.notas && (
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {linea.notas}
                      </span>
                    )}
                  </Td>
                  {/* ⚠ **Las cantidades se enseñan con `comoCantidad`, no en crudo.**
                      Viajan con seis decimales porque es la precisión del depósito, y
                      pintadas tal cual se leen «1.000000»: ocho caracteres para decir
                      «uno», y una columna donde ya no se distingue 1 de 1,5 de un
                      vistazo. Es el mismo formateador que usa el inventario. */}
                  <Td numero>
                    {comoCantidad(linea.cantidad)}{" "}
                    <span style={{ color: "var(--fg-muted)" }}>
                      {comoUnidad(linea.unidad, linea.cantidad)}
                    </span>
                  </Td>
                  <Td numero>
                    {linea.cantidad_aprobada === null ? "—" : comoCantidad(linea.cantidad_aprobada)}
                  </Td>
                  <Td numero>{comoCantidad(linea.cantidad_entregada)}</Td>
                  {puedeAtender && s.estado === "APROBADA" && (
                    <Td numero>
                      {linea.articulo_id === null ? (
                        <span
                          style={{ color: "var(--fg-muted)" }}
                          title="No está en el catálogo del depósito: sólo se puede comprar"
                        >
                          no es del depósito
                        </span>
                      ) : (
                        comoCantidad(String(hayDe(linea.id)))
                      )}
                    </Td>
                  )}
                  {puedeAtender && s.estado === "APROBADA" && (
                    <Td numero>
                      {linea.articulo_id !== null &&
                        pendiente(linea) > 0 &&
                        hayDe(linea.id) > 0 && (
                          <Boton
                            tamano="sm"
                            onClick={() => {
                              setEntregando(linea)
                              setCuanto(String(Math.min(pendiente(linea), hayDe(linea.id))))
                            }}
                          >
                            Entregar
                          </Boton>
                        )}
                    </Td>
                  )}
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>

          {ordenes.data && ordenes.data.length > 0 && (
            <div className="text-sm">
              <p className="font-medium">Se compró con</p>
              <ul>
                {ordenes.data.map((o) => (
                  <li key={o.id} className="tabular-nums">
                    {o.codigo} · {o.estado} · {o.monto_neto}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {accion.error !== null && (
            <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
              {accion.error instanceof ErrorApi
                ? accion.error.message
                : "No se pudo completar la acción."}
            </p>
          )}

          {/* Las acciones de cada estado. Lo que no se puede hacer no se pinta: un botón
              apagado hace preguntar por qué, y la respuesta está en el estado de arriba. */}
          <div className="flex flex-wrap justify-end gap-2">
            {esMia && (s.estado === "BORRADOR" || s.estado === "RECHAZADA") && (
              <Boton
                variante="primario"
                disabled={accion.isPending}
                onClick={() => accion.mutate({ que: "enviar" })}
              >
                Enviar a aprobación
              </Boton>
            )}
            {esMia && s.estado !== "ATENDIDA" && s.estado !== "ANULADA" && (
              <Boton
                disabled={accion.isPending}
                onClick={() => accion.mutate({ que: "anular", cuerpo: { motivo: null } })}
              >
                Retirar
              </Boton>
            )}
            {puedeAprobar && s.estado === "EN_APROBACION" && (
              <>
                <Boton disabled={accion.isPending} onClick={() => setRechazando(true)}>
                  Rechazar
                </Boton>
                <Boton
                  variante="primario"
                  disabled={accion.isPending}
                  onClick={() => accion.mutate({ que: "aprobar", cuerpo: {} })}
                >
                  Aprobar
                </Boton>
              </>
            )}
            {puedeAtender && s.estado === "APROBADA" && (
              <Boton
                disabled={accion.isPending}
                onClick={() => accion.mutate({ que: "cerrar", cuerpo: { notas: null } })}
              >
                Darla por atendida
              </Boton>
            )}
          </div>

          {rechazando && (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="motivo-rechazo">
                Por qué se rechaza
              </label>
              <textarea
                id="motivo-rechazo"
                className="campo"
                rows={2}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
              <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                Sin motivo, quien pidió no sabe si insistir, pedir menos o pedir otra cosa.
              </p>
              <div className="flex justify-end">
                <Boton
                  variante="primario"
                  disabled={motivo.trim().length < 3 || accion.isPending}
                  onClick={() => accion.mutate({ que: "rechazar", cuerpo: { motivo } })}
                >
                  Confirmar el rechazo
                </Boton>
              </div>
            </div>
          )}

          {entregando && (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="cuanto-entregar">
                Cuánto se entrega de «{entregando.descripcion}»
              </label>
              <input
                id="cuanto-entregar"
                className="campo tabular-nums"
                inputMode="decimal"
                value={cuanto}
                onChange={(e) => setCuanto(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Boton onClick={() => setEntregando(null)}>Cancelar</Boton>
                <Boton
                  variante="primario"
                  disabled={accion.isPending}
                  onClick={() =>
                    accion.mutate({
                      que: "entregar",
                      cuerpo: {
                        linea_id: entregando.id,
                        cantidad: paraElServidor(cuanto),
                      },
                    })
                  }
                >
                  Sacar del depósito
                </Boton>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
